import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { getUserById } from "../../api/User/userApi";
import { searchA3Articulos } from "../../api/Produccion/a3ArticulosApi";
import {
  A3_EMPRESA_DB_SOURCE_OPTIONS,
  getEmpresaA3Meta,
  normalizeEmpresaA3,
} from "../../constants/a3Empresas";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import ArticuloA3Dialog from "./ArticuloA3Dialog";

const trimCell = (value) => String(value ?? "").trim();

const articuloUsesTallas = (row) =>
  trimCell(row?.TALLAS).toUpperCase() === "T";

const formatPrice = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const ArticulosA3 = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);
  const [dbSource, setDbSource] = useState("STAGE_DESIGN");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("search");
  const [editRow, setEditRow] = useState(null);
  const [hasAccess, setHasAccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    if (!token) {
      setHasAccess(false);
      return undefined;
    }
    let userId = null;
    try {
      userId = jwtDecode(token).userId;
    } catch {
      setHasAccess(false);
      return undefined;
    }
    if (!userId) {
      setHasAccess(false);
      return undefined;
    }
    (async () => {
      try {
        const user = await getUserById(userId);
        if (!cancelled) {
          setHasAccess(
            Boolean(user?.Permiso_Articulos) || Boolean(user?.isAdmin),
          );
        }
      } catch {
        if (!cancelled) setHasAccess(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (query = search) => {
    if (hasAccess !== true) return;
    setLoading(true);
    setError("");
    try {
      const data = await searchA3Articulos(String(query ?? "").trim(), dbSource, 100);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dbSource, search, hasAccess]);

  useEffect(() => {
    if (hasAccess !== true) return;
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial y al cambiar empresa
  }, [dbSource, hasAccess]);

  const empresaLabel = useMemo(
    () => getEmpresaA3Meta(dbSource).shortLabel,
    [dbSource],
  );

  const accent = theme.palette.primary.main;
  const pageGradient =
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.grey[50];

  const heroShellSx = {
    ...pageHeroShellSx(theme, { sticky: !isMobile }),
    mb: { xs: 1.5, md: 1.25 },
    ...(theme.palette.mode === "light"
      ? {
          background: theme.palette.background.paper,
          borderColor: alpha(accent, 0.2),
          boxShadow: `0 10px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }
      : {}),
  };

  const filterFieldSx = {
    ...pageFieldSx(theme),
    minWidth: 0,
  };

  const toolbarIconSx = {
    border: "1px solid",
    borderColor: alpha(accent, 0.22),
    backgroundColor: theme.palette.background.paper,
    color: accent,
    width: { xs: 40, md: 36 },
    height: { xs: 40, md: 36 },
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    "&:hover": {
      backgroundColor: alpha(accent, 0.1),
      borderColor: accent,
      transform: "translateY(-1px)",
    },
  };

  const tableShellSx = {
    ...pageContentCardSx(theme, accent),
    p: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  };

  const tableHeadCellSx = {
    fontWeight: 800,
    fontSize: "0.68rem",
    letterSpacing: 0.45,
    textTransform: "uppercase",
    color: "text.secondary",
    bgcolor: alpha(theme.palette.background.default, 0.72),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    py: 1.25,
    whiteSpace: "nowrap",
  };

  const openCreateDialog = () => {
    setEditRow(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setEditRow(row);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditRow(null);
    setDialogMode("search");
  };

  if (hasAccess === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3, ...contentMargin }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t("articulosA3.noPermission")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 2 },
        minWidth: 0,
        ...contentMargin,
      }}
    >
      <Box sx={heroShellSx}>
        <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            alignItems={{ lg: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.14),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.28),
                }}
              >
                <Inventory2OutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...pageSectionTitleSx, fontSize: "0.62rem" }}>
                  CRM · Producción
                </Typography>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  {t("articulosA3.title")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  <Chip
                    size="small"
                    label={t("articulosA3.subtitle", { empresa: empresaLabel })}
                    sx={{
                      ...pageKpiChipSx,
                      height: 26,
                      bgcolor: alpha(accent, 0.1),
                      color: theme.palette.primary.dark,
                      border: "1px solid",
                      borderColor: alpha(accent, 0.18),
                    }}
                  />
                  {!loading ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("articulosA3.results", { count: rows.length })}
                      sx={{ ...pageKpiChipSx, height: 26 }}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openCreateDialog}
              sx={{
                ...pagePrimaryButtonSx(theme),
                alignSelf: { xs: "stretch", lg: "center" },
                whiteSpace: "nowrap",
              }}
            >
              {t("articulosA3.newArticle")}
            </Button>
          </Stack>

          <Box
            sx={{
              mt: 1.5,
              p: { xs: 1.1, md: 1.25 },
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.75),
              bgcolor: alpha(theme.palette.background.paper, 0.72),
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(160px, 0.42fr) minmax(0, 1fr) auto",
              },
              gap: { xs: 1, sm: 1.25 },
              alignItems: "end",
            }}
          >
            <TextField
              size="small"
              select
              label={t("articulosA3.company")}
              value={dbSource}
              onChange={(e) =>
                setDbSource(normalizeEmpresaA3(e.target.value, "STAGE_DESIGN"))
              }
              SelectProps={{ native: true }}
              sx={filterFieldSx}
            >
              {A3_EMPRESA_DB_SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>
            <TextField
              size="small"
              fullWidth
              label={t("articulosA3.searchLabel")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") load(search);
              }}
              sx={filterFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Button
                variant="outlined"
                onClick={() => load(search)}
                disabled={loading}
                sx={{
                  fontWeight: 800,
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2,
                  borderColor: alpha(accent, 0.35),
                  color: accent,
                  "&:hover": {
                    borderColor: accent,
                    bgcolor: alpha(accent, 0.08),
                  },
                }}
              >
                {t("articulosA3.search")}
              </Button>
              <Tooltip title={t("articulosA3.refresh")}>
                <span>
                  <IconButton
                    onClick={() => load(search)}
                    disabled={loading}
                    sx={toolbarIconSx}
                    aria-label={t("articulosA3.refresh")}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Box sx={tableShellSx}>
        {error ? (
          <Alert severity="error" sx={{ m: 1.5, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: { xs: 1.5, md: 2 },
                py: 1.15,
                borderBottom: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.75),
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {t("articulosA3.results", { count: rows.length })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t("articulosA3.clickToEdit", { defaultValue: "Clic en fila para editar" })}
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: { xs: "none", md: "calc(100dvh - 320px)" } }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colCode")}</TableCell>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colDescription")}</TableCell>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colSizes")}</TableCell>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colType")}</TableCell>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colMaterial")}</TableCell>
                    <TableCell sx={tableHeadCellSx}>{t("articulosA3.colSupplier")}</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>
                      {t("articulosA3.colPurchase")}
                    </TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>
                      {t("articulosA3.colSale")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => {
                    const codart = trimCell(row.CODART);
                    const conTallas = articuloUsesTallas(row);
                    const desc = trimCell(row.DESCART);
                    return (
                      <TableRow
                        key={`${codart}-${idx}`}
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          "&:nth-of-type(even)": {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                          "& td": {
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                            py: 1.15,
                          },
                        }}
                        onClick={() => openEditDialog(row)}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 800,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: "0.82rem",
                            color: theme.palette.primary.dark,
                          }}
                        >
                          {codart || "—"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            maxWidth: 280,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {desc || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              conTallas
                                ? t("articulosA3.withSizes")
                                : t("articulosA3.withoutSizes")
                            }
                            sx={{
                              height: 22,
                              fontWeight: 800,
                              fontSize: "0.68rem",
                              ...(conTallas
                                ? {
                                    bgcolor: alpha("#00a58a", 0.14),
                                    color: "#007a66",
                                    border: "1px solid",
                                    borderColor: alpha("#00a58a", 0.28),
                                  }
                                : {
                                    bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                    color: "text.secondary",
                                    border: "1px solid",
                                    borderColor: alpha(theme.palette.divider, 0.9),
                                  }),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                          {trimCell(row.CAR1) || "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>
                          {trimCell(row.CAR2) || "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {trimCell(row.CODPRO) || "—"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            fontVariantNumeric: "tabular-nums",
                            color: theme.palette.error.main,
                          }}
                        >
                          {formatPrice(row.PRCCOMPRA)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            fontVariantNumeric: "tabular-nums",
                            color: "#00a58a",
                          }}
                        >
                          {formatPrice(row.PRCVENTA)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!rows.length ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                        <Box
                          sx={{
                            mx: "auto",
                            maxWidth: 360,
                            py: 3,
                            px: 2,
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: alpha(theme.palette.divider, 0.9),
                            bgcolor: alpha(theme.palette.background.default, 0.55),
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" fontWeight={700}>
                            {t("articulosA3.empty")}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <ArticuloA3Dialog
        open={dialogOpen}
        onClose={closeDialog}
        initialDbSource={dbSource}
        startMode={dialogMode}
        editRow={editRow}
        onSaved={() => {
          closeDialog();
          load(search);
        }}
        onDeleted={() => {
          closeDialog();
          load(search);
        }}
      />
    </Box>
  );
};

export default ArticulosA3;
